import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth, API } from "../App";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "../components/ui/dialog";
import { toast } from "sonner";
import {
  MessageSquare,
  Plus,
  Clock,
  CheckCircle,
  AlertCircle,
  ArrowLeft,
  Send,
  User,
  Shield,
  Loader2
} from "lucide-react";

export default function SupportPage() {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNewTicketDialog, setShowNewTicketDialog] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [ticketDetail, setTicketDetail] = useState(null);
  const [replyMessage, setReplyMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);
  
  const [newTicket, setNewTicket] = useState({
    subject: "",
    message: "",
    category: "other",
    priority: "medium"
  });

  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    fetchTickets();
  }, []);

  const fetchTickets = async () => {
    try {
      const response = await axios.get(`${API}/v2/tickets`, { headers });
      setTickets(response.data.items || []);
    } catch (error) {
      console.error("Failed to fetch tickets:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateTicket = async () => {
    if (!newTicket.subject.trim() || !newTicket.message.trim()) {
      toast.error("Please fill in subject and message");
      return;
    }
    
    setSubmitting(true);
    try {
      await axios.post(`${API}/v2/tickets`, newTicket, { headers });
      toast.success("Ticket submitted successfully");
      setShowNewTicketDialog(false);
      setNewTicket({ subject: "", message: "", category: "other", priority: "medium" });
      fetchTickets();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create ticket");
    } finally {
      setSubmitting(false);
    }
  };

  const handleViewTicket = async (ticketId) => {
    try {
      const response = await axios.get(`${API}/v2/tickets/${ticketId}`, { headers });
      setTicketDetail(response.data);
      setSelectedTicket(ticketId);
    } catch (error) {
      toast.error("Failed to load ticket");
    }
  };

  const handleSendReply = async () => {
    if (!replyMessage.trim()) return;
    
    setSubmitting(true);
    try {
      await axios.post(`${API}/v2/tickets/${selectedTicket}/reply`, { message: replyMessage }, { headers });
      toast.success("Reply sent");
      setReplyMessage("");
      handleViewTicket(selectedTicket);
      fetchTickets();
    } catch (error) {
      toast.error("Failed to send reply");
    } finally {
      setSubmitting(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "open": return "bg-blue-100 text-blue-700";
      case "in_progress": return "bg-amber-100 text-amber-700";
      case "waiting_user": return "bg-purple-100 text-purple-700";
      case "resolved": return "bg-green-100 text-green-700";
      case "closed": return "bg-slate-100 text-slate-700";
      default: return "bg-slate-100 text-slate-700";
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case "urgent": return "bg-red-100 text-red-700";
      case "high": return "bg-orange-100 text-orange-700";
      case "medium": return "bg-yellow-100 text-yellow-700";
      case "low": return "bg-slate-100 text-slate-600";
      default: return "bg-slate-100 text-slate-600";
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
      </div>
    );
  }

  // Ticket Detail View
  if (selectedTicket && ticketDetail) {
    return (
      <div className="min-h-screen bg-slate-50" data-testid="ticket-detail-page">
        <header className="bg-white border-b border-slate-200 sticky top-0 z-10">
          <div className="max-w-4xl mx-auto px-6 py-4">
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="icon" onClick={() => setSelectedTicket(null)}>
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <div className="flex-1">
                <h1 className="font-semibold text-lg">{ticketDetail.ticket?.subject}</h1>
                <div className="flex items-center gap-2 mt-1">
                  <Badge className={getStatusColor(ticketDetail.ticket?.status)}>
                    {ticketDetail.ticket?.status?.replace("_", " ")}
                  </Badge>
                  <Badge className={getPriorityColor(ticketDetail.ticket?.priority)}>
                    {ticketDetail.ticket?.priority}
                  </Badge>
                  <span className="text-xs text-slate-500">
                    #{ticketDetail.ticket?.ticket_id?.slice(0, 12)}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </header>

        <main className="max-w-4xl mx-auto px-6 py-6">
          {/* Messages */}
          <div className="space-y-4 mb-6">
            {ticketDetail.messages?.map((msg) => (
              <div
                key={msg.message_id}
                className={`flex ${msg.sender_type === "user" ? "justify-end" : "justify-start"}`}
              >
                <div className={`max-w-[80%] ${msg.sender_type === "user" ? "order-2" : ""}`}>
                  <div className={`rounded-lg p-4 ${
                    msg.sender_type === "user" 
                      ? "bg-blue-500 text-white" 
                      : "bg-white border border-slate-200"
                  }`}>
                    <div className="flex items-center gap-2 mb-2">
                      {msg.sender_type === "admin" ? (
                        <Shield className="w-4 h-4 text-green-600" />
                      ) : (
                        <User className="w-4 h-4" />
                      )}
                      <span className={`text-sm font-medium ${msg.sender_type === "user" ? "text-white" : "text-slate-700"}`}>
                        {msg.sender_type === "admin" ? "Support Team" : msg.sender_name}
                      </span>
                    </div>
                    <p className={`text-sm whitespace-pre-wrap ${msg.sender_type === "user" ? "text-white" : "text-slate-600"}`}>
                      {msg.message}
                    </p>
                  </div>
                  <p className={`text-xs mt-1 ${msg.sender_type === "user" ? "text-right" : ""} text-slate-400`}>
                    {new Date(msg.created_at).toLocaleString()}
                  </p>
                </div>
              </div>
            ))}
          </div>

          {/* Reply Input */}
          {ticketDetail.ticket?.status !== "closed" && (
            <div className="bg-white rounded-lg border border-slate-200 p-4">
              <Textarea
                placeholder="Type your reply..."
                value={replyMessage}
                onChange={(e) => setReplyMessage(e.target.value)}
                rows={3}
                className="mb-3"
                data-testid="reply-input"
              />
              <div className="flex justify-end">
                <Button 
                  onClick={handleSendReply} 
                  disabled={submitting || !replyMessage.trim()}
                  data-testid="send-reply-btn"
                >
                  {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
                  Send Reply
                </Button>
              </div>
            </div>
          )}
        </main>
      </div>
    );
  }

  // Tickets List View
  return (
    <div className="min-h-screen bg-slate-50" data-testid="support-page">
      <header className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Link to="/dashboard">
                <Button variant="ghost" size="icon">
                  <ArrowLeft className="w-5 h-5" />
                </Button>
              </Link>
              <div>
                <h1 className="font-semibold text-xl">Support Center</h1>
                <p className="text-sm text-slate-500">Get help with your account</p>
              </div>
            </div>
            <Button onClick={() => setShowNewTicketDialog(true)} data-testid="new-ticket-btn">
              <Plus className="w-4 h-4 mr-2" />
              New Ticket
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-6">
        {tickets.length === 0 ? (
          <Card className="border-slate-200">
            <CardContent className="py-12 text-center">
              <MessageSquare className="w-16 h-16 mx-auto mb-4 text-slate-300" />
              <h3 className="text-lg font-medium text-slate-700 mb-2">No support tickets yet</h3>
              <p className="text-slate-500 mb-6">Have a question or issue? Create a ticket and we'll help you out.</p>
              <Button onClick={() => setShowNewTicketDialog(true)}>
                <Plus className="w-4 h-4 mr-2" />
                Create Your First Ticket
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-3">
            {tickets.map((ticket) => (
              <Card 
                key={ticket.ticket_id} 
                className="border-slate-200 hover:border-slate-300 cursor-pointer transition-colors"
                onClick={() => handleViewTicket(ticket.ticket_id)}
                data-testid={`ticket-${ticket.ticket_id}`}
              >
                <CardContent className="p-4">
                  <div className="flex items-start justify-between">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="font-medium truncate">{ticket.subject}</h3>
                        {ticket.last_reply_by === "admin" && ticket.status !== "closed" && (
                          <Badge className="bg-green-100 text-green-700 text-xs">New Reply</Badge>
                        )}
                      </div>
                      <div className="flex items-center gap-2 flex-wrap">
                        <Badge className={getStatusColor(ticket.status)}>
                          {ticket.status?.replace("_", " ")}
                        </Badge>
                        <Badge className={getPriorityColor(ticket.priority)}>
                          {ticket.priority}
                        </Badge>
                        <Badge variant="outline" className="text-xs">
                          {ticket.category}
                        </Badge>
                      </div>
                    </div>
                    <div className="text-right ml-4">
                      <p className="text-xs text-slate-400">
                        {new Date(ticket.created_at).toLocaleDateString()}
                      </p>
                      <p className="text-xs text-slate-500 mt-1">
                        #{ticket.ticket_id?.slice(7, 15)}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </main>

      {/* New Ticket Dialog */}
      <Dialog open={showNewTicketDialog} onOpenChange={setShowNewTicketDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Create Support Ticket</DialogTitle>
            <DialogDescription>
              Describe your issue and we'll get back to you as soon as possible.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <Label>Subject</Label>
              <Input
                placeholder="Brief description of your issue"
                value={newTicket.subject}
                onChange={(e) => setNewTicket({ ...newTicket, subject: e.target.value })}
                data-testid="ticket-subject-input"
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Category</Label>
                <Select
                  value={newTicket.category}
                  onValueChange={(v) => setNewTicket({ ...newTicket, category: v })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="account">Account</SelectItem>
                    <SelectItem value="payment">Payment</SelectItem>
                    <SelectItem value="task">Task Issue</SelectItem>
                    <SelectItem value="technical">Technical</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label>Priority</Label>
                <Select
                  value={newTicket.priority}
                  onValueChange={(v) => setNewTicket({ ...newTicket, priority: v })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="low">Low</SelectItem>
                    <SelectItem value="medium">Medium</SelectItem>
                    <SelectItem value="high">High</SelectItem>
                    <SelectItem value="urgent">Urgent</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div>
              <Label>Message</Label>
              <Textarea
                placeholder="Please describe your issue in detail..."
                value={newTicket.message}
                onChange={(e) => setNewTicket({ ...newTicket, message: e.target.value })}
                rows={5}
                data-testid="ticket-message-input"
              />
            </div>
            
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setShowNewTicketDialog(false)}>
                Cancel
              </Button>
              <Button onClick={handleCreateTicket} disabled={submitting} data-testid="submit-ticket-btn">
                {submitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                Submit Ticket
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
